import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { supabase } from "@/integrations/supabase/client";
import { Eyebrow } from "@/components/brand/Eyebrow";

interface Resource {
  id: string;
  title: string;
  summary: string | null;
  body: string | null;
  category: string | null;
  cover_url: string | null;
  slug: string;
}

export const Route = createFileRoute("/academy/resources/$slug")({
  head: () => ({
    meta: [
      { title: "Academy — Currency Thoughts" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ResourcePage,
});

function ResourcePage() {
  const { slug } = Route.useParams();
  const [resource, setResource] = useState<Resource | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "missing">("loading");

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("academy_resources")
        .select("id,title,summary,body,category,cover_url,slug,published")
        .eq("slug", slug)
        .eq("published", true)
        .maybeSingle();
      if (!data) {
        setStatus("missing");
        return;
      }
      setResource(data as Resource);
      setStatus("ready");
    })();
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 font-sans text-charcoal-500">
        Loading…
      </div>
    );
  }

  if (status === "missing" || !resource) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <Eyebrow align="center">Academy</Eyebrow>
        <h1 className="mt-4 font-display text-navy-800 text-[32px]">
          This article isn’t available
        </h1>
        <p className="mt-3 font-sans text-charcoal-500">
          It may have been unpublished or moved.
        </p>
        <Link
          to="/academy"
          className="mt-6 inline-block font-sans text-[12px] uppercase tracking-[0.18em] text-bronze-600 hover:text-navy-800 border-b border-bronze-600/40"
        >
          Back to Academy
        </Link>
      </div>
    );
  }

  return (
    <article className="max-w-3xl mx-auto px-6 py-12 md:py-16">
      {resource.category && <Eyebrow>{resource.category}</Eyebrow>}
      <h1
        className="mt-4 font-display font-semibold text-navy-800"
        style={{ fontSize: "44px", letterSpacing: "-0.015em", lineHeight: 1.15 }}
      >
        {resource.title}
      </h1>
      {resource.summary && (
        <p className="mt-4 font-sans text-[17px] text-charcoal-500 leading-relaxed">
          {resource.summary}
        </p>
      )}
      {resource.cover_url && (
        <img
          src={resource.cover_url}
          alt=""
          className="mt-8 w-full rounded-[4px] border border-bronze-600/20"
        />
      )}

      <div className="mt-10 bg-cream-50 border border-bronze-600/20 rounded-[4px] p-8 shadow-soft">
        <div className="resource-prose font-sans text-[16px] text-charcoal-900 leading-[1.75]">
          {resource.body ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {resource.body}
            </ReactMarkdown>
          ) : (
            <p className="text-charcoal-500 italic">
              This article is being prepared.
            </p>
          )}
        </div>
      </div>

      <div className="mt-10 text-center">
        <Link
          to="/client/education"
          className="font-sans text-[12px] uppercase tracking-[0.18em] text-bronze-600 hover:text-navy-800 border-b border-bronze-600/40 pb-0.5"
        >
          Back to Education
        </Link>
      </div>

      <style>{`
        .resource-prose h2 { font-family: var(--font-display,"Playfair Display",serif); font-weight:600; color:#002D68; font-size:26px; margin:1.8em 0 .5em; }
        .resource-prose h3 { font-family: var(--font-display,"Playfair Display",serif); font-weight:600; color:#0A3369; font-size:20px; margin:1.4em 0 .4em; }
        .resource-prose p { margin:.9em 0; }
        .resource-prose ul, .resource-prose ol { margin:.8em 0 .8em 1.2em; }
        .resource-prose li { margin:.3em 0; }
        .resource-prose strong { color:#0A3369; font-weight:600; }
        .resource-prose hr { border:0; border-top:1px solid rgba(168,139,92,.3); margin:2em 0; }
        .resource-prose a { color:#A88B5C; text-decoration:underline; }
      `}</style>
    </article>
  );
}